import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { ApiError, api, post } from "../api";
import { Notice, StudentShell } from "../components/Layout";
import { QuickSignIn } from "../components/QuickSignIn";
import { useI18n } from "../i18n";
import type { MeResponse, PublicConfig, Session } from "../types";

type BillingIntent = "dropin" | "pass_pending";
interface CheckinResult {
  ok: true; duplicate: boolean; chargeType: "pass" | "unlimited" | "dropin" | "pass_pending";
  amountMinor?: number; currency?: string; session: Session;
}

export interface CheckinResolution {
  ok: true;
  preferredSession: Session;
  eligibleSessions: Session[];
  state: "ready" | "selection_required" | "closed";
}

type Translate = ReturnType<typeof useI18n>["t"];
function translatedError(reason: unknown, t: Translate, fallback: "error" | "invalidQr" | "invalidClassCode" = "error") {
  if (reason instanceof ApiError) {
    if (reason.code === "CHECKIN_CLOSED") return t("checkinClosedHelp");
    if (reason.code === "SESSION_SELECTION_REQUIRED" || reason.code === "SESSION_NOT_ELIGIBLE") return t("chooseAvailableClass");
    if (reason.code === "BILLING_INTENT_REQUIRED") return t("noPass");
    if (reason.status === 429) return t("tooManyCodeAttempts");
  }
  return t(fallback);
}

function money(amountMinor: number, currency: string, language: "zh" | "en") {
  return new Intl.NumberFormat(language === "zh" ? "zh-HK" : "en-GB", {
    style: "currency", currency, minimumFractionDigits: amountMinor % 100 ? 2 : 0,
  }).format(amountMinor / 100);
}

export function NewStudentWelcome({ registrationUrl, onExistingAccount }: {
  registrationUrl: string; onExistingAccount: () => void;
}) {
  const { t } = useI18n();
  return <div className="welcome-flow">
    <h2>{t("welcome")}</h2>
    <strong>{t("newHere")}</strong>
    <p>{t("registrationFirst")}<br />{t("registrationAfter")}</p>
    {registrationUrl
      ? <a className="primary-link wide" href={registrationUrl} target="_blank" rel="noreferrer">{t("completeRegistration")}</a>
      : <Notice>{t("registrationSoon")}</Notice>}
    <button className="secondary-button wide" onClick={onExistingAccount}>{t("alreadyAccount")}</button>
  </div>;
}

export function DropInSuccessMessage() {
  const { t } = useI18n();
  return <>
    <p>{t("dropinRecorded")}</p>
    <p className="dropin-pass-note">{t("passesAvailable")}<br />{t("contactInstructorPass")}</p>
  </>;
}

export function PassPendingSuccessMessage() {
  const { t } = useI18n();
  return <><p>{t("passPendingRecorded")}</p><span className="charge-badge charge-badge-pass_pending">{t("passPending")}</span></>;
}

export function SessionChooser({ sessions, working, online, onChoose }: {
  sessions: Session[]; working: boolean; online: boolean; onChoose: (sessionId: string) => void;
}) {
  const { t } = useI18n();
  return <div className="session-chooser">
    <h3>{t("chooseClass")}</h3>
    <div className="session-choice-list">{sessions.map((session) => <button key={session.session_id}
      className="primary-button wide" disabled={working || !online} onClick={() => onChoose(session.session_id)}>
      <strong>{session.title}</strong><span>{session.start_time}–{session.end_time}</span>
    </button>)}</div>
  </div>;
}

export function NoPassChoice({ session, amountMinor, currency, working, online, onChoose }: {
  session: Session; amountMinor: number; currency: string; working: boolean; online: boolean;
  onChoose: (intent: BillingIntent) => void;
}) {
  const { t, language } = useI18n();
  return <div className="no-pass-choice">
    <h3>{t("noPass")}</h3>
    <p>{t("noPassQuestion")}</p>
    <strong className="no-pass-class inline-meta"><span>{session.title}</span><span>{session.start_time}–{session.end_time}</span></strong>
    <div className="no-pass-actions">
      <button className="primary-button wide" disabled={working || !online} onClick={() => onChoose("dropin")}>
        {t("useDropIn")} – {money(amountMinor, currency, language)}
      </button>
      <button className="secondary-button wide" disabled={working || !online} onClick={() => onChoose("pass_pending")}>
        {t("buyPassIntent")}
      </button>
    </div>
  </div>;
}

export function CheckinPage() {
  const { t } = useI18n();
  const [search] = useState(() => new URLSearchParams(window.location.search));
  const [challenge, setChallenge] = useState(search.get("challenge") || "");
  const [checkinMethod, setCheckinMethod] = useState<"qr" | "code">(search.get("method") === "code" ? "code" : "qr");
  const [resolution, setResolution] = useState<CheckinResolution | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [showReconnect, setShowReconnect] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<CheckinResult | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const submissionKey = useRef("");

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update); window.addEventListener("offline", update);
    return () => { window.removeEventListener("online", update); window.removeEventListener("offline", update); };
  }, []);
  useEffect(() => {
    let active = true;
    setLoading(true); setResolution(null); setResult(null); setSelectedSessionId("");
    setError(""); setMessage(""); submissionKey.current = "";
    Promise.allSettled([
      challenge ? api<CheckinResolution>("/api/check-in/session?challenge=" + encodeURIComponent(challenge)) : Promise.resolve(null),
      api<MeResponse>("/api/me"),
      api<PublicConfig>("/api/config"),
    ]).then(([resolutionResult, meResult, configResult]) => {
      if (!active) return;
      if (resolutionResult.status === "fulfilled" && resolutionResult.value) setResolution(resolutionResult.value);
      if (resolutionResult.status === "rejected") setError(translatedError(resolutionResult.reason, t, "invalidQr"));
      if (meResult.status === "fulfilled") setMe(meResult.value);
      if (configResult.status === "fulfilled") setConfig(configResult.value);
      else setError(translatedError(configResult.reason, t));
      setLoading(false);
    });
    return () => { active = false; };
  }, [challenge, t]);

  async function resolveCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError(""); setMessage(""); setWorking(true);
    const code = String(new FormData(event.currentTarget).get("code") || "").replace(/\D/g, "");
    try {
      const data = await post<{ ok: true; challenge: string }>("/api/check-in/resolve-code", { code });
      setChallenge(data.challenge); setCheckinMethod("code");
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set("challenge", data.challenge); nextUrl.searchParams.set("method", "code");
      history.replaceState(null, "", nextUrl);
    } catch (reason) { setError(translatedError(reason, t, "invalidClassCode")); }
    finally { setWorking(false); }
  }
  const checkin = useCallback(async (sessionId?: string, billingIntent?: BillingIntent) => {
    if (!challenge || !online || working) return;
    setError(""); setMessage(""); setWorking(true);
    try {
      setResult(await post<CheckinResult>("/api/check-ins", { challenge, method: checkinMethod, sessionId, billingIntent }));
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === "BILLING_INTENT_REQUIRED") {
        setMe((current) => current ? { ...current, checkin: { hasActivePass: false } } : current);
        if (sessionId) setSelectedSessionId(sessionId);
      } else setError(translatedError(reason, t));
    } finally { setWorking(false); }
  }, [challenge, checkinMethod, online, t, working]);
  useEffect(() => {
    if (loading || !me?.checkin.hasActivePass || !challenge || !online || result || resolution?.state !== "ready"
      || resolution.eligibleSessions.length !== 1) return;
    const target = resolution.eligibleSessions[0];
    const key = `${me.student.studentId}:${challenge}:${target.session_id}`;
    if (submissionKey.current === key) return;
    submissionKey.current = key;
    void checkin(target.session_id);
  }, [challenge, checkin, loading, me, online, resolution, result]);
  async function recover(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError(""); setMessage(""); setWorking(true);
    const email = String(new FormData(event.currentTarget).get("email") || "");
    try {
      await post<{ message: string }>("/api/student/recovery", { email, challenge, method: checkinMethod });
      setMessage(t("recoverySent"));
    } catch (reason) { setError(translatedError(reason, t)); }
    finally { setWorking(false); }
  }

  const reconnect = <IdentityRecovery onSubmit={recover} working={working} />;
  const selectedSession = resolution?.eligibleSessions.find((session) => session.session_id === selectedSessionId);
  const soleSession = resolution?.state === "ready" ? resolution.eligibleSessions[0] : undefined;
  const billingSession = me && !me.checkin.hasActivePass ? selectedSession ?? soleSession : undefined;
  const preferred = billingSession ?? soleSession ?? resolution?.preferredSession;
  return <StudentShell>
    {!online && <Notice kind="error">{t("internetRequired")}</Notice>}
    {loading && <div className="loading-card" aria-live="polite">{t("loading")}</div>}
    {!loading && result && <section className="student-card success-card">
      <div className="success-mark" aria-hidden="true">✓</div>
      <h2>{result.duplicate ? t("duplicate") : t("complete")}</h2>
      <p className="inline-meta"><span>{result.session.title}</span><span>{result.session.start_time}</span></p>
      {result.chargeType === "pass" ? <p>{t("passDeducted")}</p>
        : result.chargeType === "unlimited" ? <p>{t("unlimitedRecorded")}</p>
          : result.chargeType === "pass_pending" ? <PassPendingSuccessMessage /> : <DropInSuccessMessage />}
      <a className="primary-link" href="/me">{t("viewRecords")}</a>
    </section>}
    {!loading && !result && <section className="student-card">
      {search.has("authContextExpired") && <Notice>{t("invalidQr")}</Notice>}
      {!me && <QuickSignIn challenge={challenge} method={checkinMethod} onEmail={() => setShowReconnect(true)} />}
      {preferred ? <>
        <span className="class-pill">{resolution?.state === "closed" ? t("closed") : t("open")}</span>
        <h2>{preferred.title}</h2>
        <div className="class-context"><span>{preferred.class_date}</span><span>{preferred.start_time}</span>{preferred.room && <span>{preferred.room}</span>}</div>
        {error && <Notice kind="error">{error}</Notice>}{message && <Notice>{message}</Notice>}
        {me ? <>
          <p className="eyebrow">{t("confirmAs")}</p>
          <p className="identity-name">{me.student.displayName}</p>
          {resolution?.state === "closed"
            ? <Notice kind="info">{t("checkinClosedHelp")}</Notice>
            : billingSession && config
              ? <NoPassChoice session={billingSession} amountMinor={config.dropInPriceMinor} currency={config.currency}
                working={working} online={online} onChoose={(intent) => void checkin(billingSession.session_id, intent)} />
              : resolution?.state === "selection_required"
                ? <SessionChooser sessions={resolution.eligibleSessions} working={working} online={online}
                  onChoose={(sessionId) => me.checkin.hasActivePass ? void checkin(sessionId) : setSelectedSessionId(sessionId)} />
                : <div className="checkin-progress" aria-live="polite">
                  <p>{working ? t("recordingCheckin") : error ? t("notRecorded") : t("preparingCheckin")}</p>
                  {error && <button className="primary-button wide" onClick={() => void checkin(preferred.session_id)} disabled={!online || working}>{t("retryCheckin")}</button>}
                </div>}
          <div className="secondary-actions"><a href="/me">{t("viewRecords")}</a></div>
        </> : showReconnect
          ? reconnect
          : <NewStudentWelcome registrationUrl={config?.studentRegistrationFormUrl || ""} onExistingAccount={() => setShowReconnect(true)} />}
      </> : <>
        <h2>{t("classCode")}</h2>
        {error && <Notice kind="error">{error}</Notice>}{message && <Notice>{message}</Notice>}
        <form onSubmit={resolveCode} className="code-form">
          <label htmlFor="class-code">{t("classCode")}</label>
          <div><input id="class-code" name="code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required placeholder="123456" />
            <button disabled={working}>{t("useCode")}</button></div>
        </form>
        {me ? <a className="secondary-link" href="/me">{t("viewRecords")}</a> : showReconnect ? reconnect
          : <NewStudentWelcome registrationUrl={config?.studentRegistrationFormUrl || ""} onExistingAccount={() => setShowReconnect(true)} />}
      </>}
    </section>}
  </StudentShell>;
}

function IdentityRecovery({ onSubmit, working }: {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void; working: boolean;
}) {
  const { t } = useI18n();
  return <div className="recovery-panel">
    <p>{t("identityRequired")}</p>
    <form onSubmit={onSubmit} className="stack-form">
      <label htmlFor="recovery-email">{t("email")}</label>
      <input id="recovery-email" name="email" type="email" autoComplete="email" required />
      <button className="secondary-button" disabled={working}>{t("sendRecovery")}</button>
    </form>
  </div>;
}
